/*
  Can be used by react-hook-forms to add single date selector with the default calendar icon
*/
/* eslint-disable @willow/import-date-picker */
import { CalendarOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ErrorMessage } from '@hookform/error-message';
import classNames from 'classnames';
import { get } from 'lodash';
import { ReactNode } from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import DatePicker, { DatePickerProps } from 'react-datepicker';
import { Control, FieldPathByValue, FieldValues, useController } from 'react-hook-form';

import { formatDateFromDate } from '@willow/shared-iso';
import { Temporal } from '@willow/types-iso';

import { Hint } from '../Hint';

import 'react-datepicker/dist/react-datepicker.css';

interface Props<T extends FieldValues> {
  fieldName: FieldPathByValue<T, Date | Temporal.PlainDate | undefined | null>;
  control: Control<T>;
  props?: DatePickerProps;
  handleOnChange?: (e: Date | Temporal.PlainDate | null) => void;
  hideErrorMessage?: boolean;
  label?: ReactNode;
  optional?: boolean;
  hint?: ReactNode;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  displayValue?: boolean;
  showMonthYearPicker?: boolean;
  showMonthDropdown?: boolean;
  showYearDropdown?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  tooltip?: string;
  tooltipLabel?: string;
  popperPlacement?: DatePickerProps['popperPlacement'];
  showCalendarIcon?: boolean;
}

export function ControlledSingleDatePicker<T extends FieldValues>({
  fieldName,
  control,
  props,
  handleOnChange,
  hideErrorMessage = false,
  label,
  optional,
  hint,
  required,
  placeholder = 'Select Date',
  disabled,
  displayValue,
  showMonthYearPicker = false,
  showMonthDropdown = false,
  showYearDropdown = false,
  minDate,
  maxDate,
  className,
  tooltip,
  tooltipLabel,
  popperPlacement,
  showCalendarIcon = true,
}: Props<T>) {
  const {
    field,
    formState: { errors },
  } = useController({ control, name: fieldName });

  // We want this explicit type here which is compatible because the inferred
  // type of `field.value` gets weir below when we `instanceof` check it.
  // Unfortunately prefer-destructuring blows it away so we need to disable it
  // to retain the type.
  //
  // eslint-disable-next-line prefer-destructuring
  const value: Date | Temporal.PlainDate | null | undefined = field.value;

  const selectedValue =
    value != null && value instanceof Temporal.PlainDate
      ? new Date(
          value
            .toZonedDateTime({
              timeZone: Temporal.Now.timeZoneId(),
              plainTime: '00:00:00',
            })
            .toInstant()
            .toJSON(),
        )
      : value;

  return (
    <>
      {label && (
        <Form.Label className="u-fs-2 fw-bold">
          {label}
          {optional && <span className="u-color-bark3 ps-1 fw-normal"> (optional)</span>}
          {required && <span className="u-color-red1 ps-1">*</span>}
          {tooltipLabel && (
            <OverlayTrigger trigger={['click', 'focus', 'hover']} rootClose overlay={<Tooltip>{tooltipLabel}</Tooltip>}>
              <InfoCircleOutlined className="ms-2" />
            </OverlayTrigger>
          )}
        </Form.Label>
      )}
      <div className="d-flex align-items-center">
        {displayValue ? (
          <div className="u-fs-2">{value ? formatDateFromDate(value) : 'N/A'}</div>
        ) : (
          <>
            <DatePicker
              className={classNames('form-control', className, {
                'u-border-color-red1': !!get(errors, fieldName),
              })}
              popperClassName="u-z-200"
              wrapperClassName="w-100"
              name={fieldName}
              dateFormat={['MM/dd/yyyy', 'M/d/yyyy', 'MM/dd/yy', 'M/d/yy']} // capture user-intended formats so that react-datepicker doesn't try to apply its own parsing guess
              strictParsing // parser only fires on a complete dateFormat match (by default is after each keystroke)
              selected={selectedValue}
              onChange={(date: Date | null | [Date | null, Date | null] | Date[]) => {
                let realDate;
                if (Array.isArray(date)) {
                  realDate = date[0] ?? null;
                } else {
                  realDate = date ?? null;
                }
                field.onChange({
                  target: {
                    name: fieldName,
                    value: realDate,
                  },
                });

                if (handleOnChange) handleOnChange(realDate);
              }}
              placeholderText={placeholder}
              disabled={disabled}
              showMonthYearPicker={showMonthYearPicker}
              showMonthDropdown={showMonthDropdown}
              showYearDropdown={showYearDropdown}
              autoComplete="off"
              minDate={minDate}
              maxDate={maxDate}
              popperPlacement={popperPlacement}
              {...props}
            />

            {showCalendarIcon && <CalendarOutlined className="ms-n4 pe-2 u-z-0 u-pointer-events-none" />}
            {tooltip && (
              <OverlayTrigger trigger={['click', 'focus', 'hover']} rootClose overlay={<Tooltip>{tooltip}</Tooltip>}>
                <InfoCircleOutlined className="ms-2" />
              </OverlayTrigger>
            )}
          </>
        )}
      </div>
      {hint && <Hint>{hint}</Hint>}
      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any} // This *should* work without casting but it doesn't
          render={({ message }) => <div className="u-color-red1 u-fs-2 mt-1">{message}</div>}
        />
      )}
    </>
  );
}
